import {COPILOT_CLI_SLUG} from '@github-ui/agent-sessions/utils/agent-helpers'

import type {IssueAgentTask} from '../contexts/IssueAgentTasksContext'

/**
 * Returns true when the given task was produced by a Copilot CLI session.
 *
 * CLI tasks are identified by having `copilot-developer-cli` as the slug of
 * their first agent collaborator — the same identification used by
 * `resolveAgentProfileForAPITask` in @github-ui/agent-sessions.
 */
export function isCliTask(task: Pick<IssueAgentTask, 'agent_collaborators'>): boolean {
  const collaborator = task.agent_collaborators?.[0]
  return collaborator?.slug === COPILOT_CLI_SLUG
}

/**
 * Returns true when the current viewer is allowed to open the deep-link
 * session-log panel for the given task.
 *
 * For Copilot CLI tasks we surface the lifecycle pill on the issue header to
 * everyone who can read the issue (planning-tracking#2952), but the raw
 * session log itself is creator-only — matching the visibility contract
 * documented for CLI remote control:
 * https://docs.github.com/en/copilot/concepts/agents/copilot-cli/about-remote-control
 *
 * Cloud-agent tasks (and any non-CLI task) keep their existing repo-level
 * visibility model, so anyone who can see the assignment can open the panel.
 */
export function canViewerOpenTaskPanel(
  task: Pick<IssueAgentTask, 'agent_collaborators' | 'creator'>,
  viewerId: number | undefined,
): boolean {
  if (!isCliTask(task)) {
    return true
  }
  if (viewerId === undefined) {
    return false
  }
  return task.creator.id === viewerId
}
